import {
  Action,
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { RootState } from "store";
import { asyncMutations, mutationBuilder } from "./createSalesOrder.mutations";

export interface SalesOrderForm {
  customerShipTo: {
    id: string;
    name: string;
  } | null;
  referenceWithRevision: number;
  price: number;
  parts?: Array<{
    partId: string;
    name: string;
    quantity: number;
    price: number;
  }>;
}

export interface State {
  createSalesOrderLoading: boolean;
  initLoading: boolean;
  isValid: boolean;
  isDirty: boolean;
  open: boolean;
  errors: Record<string, string>;
  formValues: SalesOrderForm;
}

interface Actions extends SliceCaseReducers<State> {
  setOpen: (state: State, action: PayloadAction<{ open: boolean }>) => State;
  setValue: <K extends keyof SalesOrderForm>(
    state: State,
    action: PayloadAction<{
      field: K;
      value: SalesOrderForm[K];
      shouldValidate?: boolean;
    }>
  ) => State;
  validateForm: (state: State, action: Action) => State;
  handleClose: (state: State, action: Action) => State;
}

interface Selectors {
  selectCreateSalesOrderOpen: (state: RootState) => boolean;
  selectCreateSalesOrderState: (state: RootState) => State;
  selectCreateSalesOrderForm: (state: RootState) => SalesOrderForm;
}

export const initialState: State = {
  createSalesOrderLoading: false,
  initLoading: false,
  isValid: true,
  isDirty: false,
  open: false,
  errors: {},
    formValues: {
    customerShipTo: null,
    referenceWithRevision: 0,
    price: 0,
    parts: [],
  }
};

const actions: Actions = {
  setOpen: (state, { payload: { open } }) => {
    state.open = open;
    return state;
  },
  setValue: (state, { payload: { field, value, shouldValidate } }) => {
    state.formValues[field] = value;
    state.isDirty = true;

    if (!shouldValidate && state.isValid) return state;

    const errors: Record<string, string> = {};
    if (!state.formValues.customerShipTo?.id) errors.customerShipTo = "Customer is required";
    if (!state.formValues.referenceWithRevision) errors.referenceWithRevision = "Reference is required";
    if (state.formValues.price <= 0) errors.price = "Price must be greater than 0";

    state.errors = errors;
    state.isValid = Object.keys(errors).length === 0;

    return state;
  },
  validateForm: (state) => {
    const errors: Record<string, string> = {};
    if (!state.formValues.customerShipTo?.id) errors.customerShipTo = "Customer is required";
    if (!state.formValues.referenceWithRevision) errors.referenceWithRevision = "Reference is required";
    if (state.formValues.price <= 0) errors.price = "Price must be greater than 0";

    state.errors = errors;
    state.isValid = Object.keys(errors).length === 0;

    return state;
  },
  handleClose: (state) => {
    state.open = false;
    state.initLoading = false;
    state.createSalesOrderLoading = false;
    state.isValid = true;
    state.isDirty = false;
    state.errors = {};
    state.formValues = {
    customerShipTo: null,
    referenceWithRevision: 0,
    price: 0,
    parts: [],
  };
    return state;
  },
};

const selectors: Selectors = {
  selectCreateSalesOrderOpen: ({ createSalesOrder }) => createSalesOrder.open,
  selectCreateSalesOrderState: ({ createSalesOrder }) => createSalesOrder,
  selectCreateSalesOrderForm: ({ createSalesOrder }) => createSalesOrder.formValues,
};

const storeBase = createSlice<State, Actions>({
  name: "createSalesOrder",
  initialState,
  reducers: actions,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    mutationBuilder(builder);
  },
});

export default storeBase.reducer;

export const { setOpen, setValue, validateForm, handleClose } = storeBase.actions;
export const { createSalesOrder } = asyncMutations;
export const {
  selectCreateSalesOrderOpen,
  selectCreateSalesOrderState,
  selectCreateSalesOrderForm,
} = selectors;